import React from 'react'
import FareFamilySelectModal from 'components/Flights/V2/FareFamily/FareFamilySelectModal'
import { generateFlightCheckoutItem } from 'lib/checkout/flights/cart'
import { generatePassengersFromOccupants } from 'checkout/lib/utils/flights/passenger'
import { FlightsFareTypes } from 'constants/flight'
import type { FlightSearchParams } from 'hooks/useSearchFlights'
import type { ModalContextType } from 'contexts/ModalContext'
import {
  mapCheckoutFlightItemToTripChatFlightCartPayload,
  type TripChatFlightCartPayloadItem,
} from 'tripPlanner/api/flightCart'

export interface PreparedTripChatFlightCartPayload {
  checkoutItem: App.Checkout.FlightItem
  cartPayload: TripChatFlightCartPayloadItem
  outboundJourney: App.JourneyV2
  returningJourney?: App.JourneyV2
  searchId: string
  sessionId?: string
  searchExpiry: number
  occupants: App.Occupants
  fareType: 'oneWay' | 'return'
  fareFamilies: Array<App.FlightFareFamily | undefined>
}

export type PrepareFlightCheckoutItemResult =
  | { status: 'success'; prepared: PreparedTripChatFlightCartPayload }
  | { status: 'missing_search_id' | 'cancelled' | 'missing_search_params' }

export interface PrepareSelectedFlightCheckoutItemArgs {
  departingJourney: App.JourneyV2
  returningJourney?: App.JourneyV2
  searchParams: FlightSearchParams
  searchId: string | undefined
  sessionId: string | undefined
  searchExpiry: number
  showModal: ModalContextType
}

interface FareFamilyModalResult {
  fareFamily?: App.FlightFareFamily
  returningFareFamily?: App.FlightFareFamily
  addToUserCart?: boolean
}

async function selectFareFamily(args: {
  journey: App.JourneyV2
  departingJourney?: App.JourneyV2
  departingFareFamily?: App.FlightFareFamily
  occupants: App.Occupants
  searchId: string
  sessionId: string | undefined
  showModal: ModalContextType
}): Promise<App.FlightFareFamily | undefined | 'cancelled'> {
  const { journey, departingJourney, departingFareFamily, occupants, searchId, sessionId, showModal } = args
  if (!journey.fareFamily) return undefined

  const result = await showModal<FareFamilyModalResult>(
    React.createElement(FareFamilySelectModal, {
      journey,
      departingJourney,
      departingFareFamily,
      occupancies: occupants,
      searchId,
      sessionId,
      relativePrice: journey.price.all.totalFare,
      isStandalone: true,
    }),
  )

  if (!result) return 'cancelled'
  return result.fareFamily
}

export async function prepareSelectedFlightCheckoutItem({
  departingJourney,
  returningJourney,
  searchParams,
  searchId,
  sessionId,
  searchExpiry,
  showModal,
}: PrepareSelectedFlightCheckoutItemArgs): Promise<PrepareFlightCheckoutItemResult> {
  if (!searchId) return { status: 'missing_search_id' }

  const firstItineraryLeg = searchParams.itinerary[0]
  if (!firstItineraryLeg) return { status: 'missing_search_params' }

  const occupants = searchParams.occupancies[0] ?? { adults: 1 }
  const passengers = generatePassengersFromOccupants(occupants)
  const fareType = returningJourney ? 'return' : 'oneWay'

  // Multi-city legs auto-pick their fare (no fare-family selection), matching how
  // multi-city flights are booked on the standalone flights page. Showing the
  // fare-family modal here was blocking multi-city checkout from the cart.
  const isMultiCity = searchParams.fareType === FlightsFareTypes.MULTI_CITY

  const departingFareFamily = isMultiCity ? undefined : await selectFareFamily({
    journey: departingJourney,
    occupants,
    searchId,
    sessionId,
    showModal,
  })
  if (departingFareFamily === 'cancelled') return { status: 'cancelled' }

  const returningFareFamily = !isMultiCity && returningJourney ? await selectFareFamily({
    journey: returningJourney,
    departingJourney,
    departingFareFamily,
    occupants,
    searchId,
    sessionId,
    showModal,
  }) : undefined
  if (returningFareFamily === 'cancelled') return { status: 'cancelled' }

  const flights = returningJourney ? [departingJourney, returningJourney] : [departingJourney]
  const fareFamilies = returningJourney
    ? [departingFareFamily, returningFareFamily]
    : [departingFareFamily]

  // Derive the route from the selected journey, not searchParams.itinerary[0]:
  // a multi-city leg's saved searchParams hold the FULL itinerary, so itinerary[0]
  // is the first leg (e.g. SYD→MEL) even when this leg is MEL→PER. Using it sent
  // the wrong departure airport and the backend rejected it with ROUTE_MISMATCH.
  const departingFlights = departingJourney.flightGroup.flights
  const originAirportCode = departingFlights[0]?.departureAirport ?? firstItineraryLeg.departureAirport
  const destinationAirportCode =
    departingFlights[departingFlights.length - 1]?.arrivalAirport ?? firstItineraryLeg.arrivalAirport

  const checkoutItem = generateFlightCheckoutItem({
    originAirportCode,
    destinationAirportCode,
    occupants,
    passengers,
    fareType,
    viewType: fareType === 'oneWay' ? 'OneWay' : 'TwoOneWays',
    searchId,
    flights,
    baggage: [],
    fareFamilies,
    bundledItemIds: [],
    expiry: searchExpiry,
  })

  return {
    status: 'success',
    prepared: {
      checkoutItem,
      cartPayload: mapCheckoutFlightItemToTripChatFlightCartPayload(checkoutItem),
      outboundJourney: departingJourney,
      returningJourney,
      searchId,
      sessionId,
      searchExpiry,
      occupants,
      fareType,
      fareFamilies,
    },
  }
}
